import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

// Overlays (a select's options, a tooltip, the leave dialog itself) render outside the form but
// belong to it, so clicks inside them are let through.
const OVERLAY_SELECTOR = "[data-radix-popper-content-wrapper], [role='dialog'], [data-react-aria-top-layer]";

const BLOCKED_EVENTS = ["pointerdown", "mousedown", "pointerup", "mouseup", "click"] as const;

/**
 * Holds back clicks outside a form the user started filling in. The first one is swallowed and
 * `nudged` turns on, so the form can ask to be finished; the next one is swallowed too and kept as
 * `pendingLeave`, so the caller can ask whether to discard. `leave` replays that click once the
 * caller has cleared the form, which turns `active` off.
 */
export const useUnfinishedFormGuard = (formRef: RefObject<HTMLElement | null>, active: boolean) => {
  const [nudged, setNudged] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<HTMLElement | null>(null);
  const nudgedRef = useRef(false);

  useEffect(() => {
    if (!active) {
      nudgedRef.current = false;
      setNudged(false);
      return;
    }

    const isOutside = (target: EventTarget | null) =>
      target instanceof Element && !formRef.current?.contains(target) && !target.closest(OVERLAY_SELECTOR);

    const block = (event: Event) => {
      if (!isOutside(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.type !== "pointerdown") return;
      if (!nudgedRef.current) {
        nudgedRef.current = true;
        setNudged(true);
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setPendingLeave(event.target as HTMLElement);
      }
    };

    for (const type of BLOCKED_EVENTS) document.addEventListener(type, block, true);
    return () => {
      for (const type of BLOCKED_EVENTS) document.removeEventListener(type, block, true);
    };
  }, [active, formRef]);

  const stay = useCallback(() => setPendingLeave(null), []);

  const leave = useCallback(() => {
    const target = pendingLeave;
    setPendingLeave(null);
    // Replayed after the guard has been torn down by the caller clearing the form.
    if (target) setTimeout(() => target.click(), 0);
  }, [pendingLeave]);

  return { nudged, pendingLeave: !!pendingLeave, stay, leave };
};
